import { useEffect, useRef, useState } from 'react';
import type { FunctionComponent } from 'react';

import type { ForwardProps } from '../../types';
import Grid from '../Grid';
import Text from '../Text';
import { useArrows, useElement, useI18n } from '../../hooks';
import Button from '../Button';
import Flex from '../Flex';

import {
  StepMarker,
  StyledBulletWrapper,
  StyledStepText,
  StyledVerticalStepMarkers
} from './FormProgress.styles';
import type { MultiStepFormProps } from './MultiStepForm.types';

interface FormProgressProps {
  steps: MultiStepFormProps['steps'];
  currentStepId: MultiStepFormProps['currentStepId'];
}

const VerticalFormProgress: FunctionComponent<FormProgressProps & ForwardProps> = ({
  steps,
  currentStepId,
  ...restProps
}) => {
  const t = useI18n();

  const curIdx = steps.findIndex(step => step.id === currentStepId);
  const curStep = steps[curIdx];

  const [currentStepEl, setCurrentStepEl] = useElement();
  const [stepEls, setStepEls] = useState<Record<string, HTMLElement>>({});
  const stepsRef = useRef(null);

  useArrows(stepsRef, {
    selector: 'button[aria-label]',
    cycle: false,
    dir: 'left-right',
    initialFocusElement: currentStepEl
  });

  useArrows(stepsRef, {
    selector: 'button[aria-label]',
    cycle: false,
    dir: 'up-down',
    initialFocusElement: currentStepEl
  });

  useEffect(() => {
    if (stepEls[curStep.id] && stepEls[curStep.id] !== currentStepEl) {
      setCurrentStepEl(stepEls[curStep.id]);
    }
  }, [Object.keys(stepEls), curStep]);

  return (
    <StyledVerticalStepMarkers ref={stepsRef} {...restProps}>
      {steps.map((step, index) => {
        const isCurrent = index === curIdx;
        return (
          <Grid
            key={step.id}
            as='li'
            container={{
              cols: '2rem minmax(0, 1fr)',
              colGap: 1
            }}
          >
            <Flex
              container={{ alignItems: 'center', justify: 'center' }}
              as={StyledBulletWrapper}
              hasNext={index !== steps.length - 1}
              prior={index < curIdx}
            >
              <StepMarker
                ref={(el: HTMLElement | null) => {
                  setStepEls(curr => {
                    const newEls = { ...curr };
                    if (!el && newEls[step.id]) {
                      delete newEls[step.id];
                      return newEls;
                    }
                    if (el) return { ...newEls, [step.id]: el };
                    return newEls;
                  });
                }}
                id={step.id}
                aria-label={`${step.name} - ${t('step_num', [index + 1, steps.length])}`}
                aria-current={isCurrent ? 'step' : undefined}
                tabIndex={isCurrent ? 0 : -1}
                current={isCurrent}
                completed={step.completed}
                prior={index < curIdx}
                depth={step.depth}
                onClick={step.onMarkerClick}
                clickable={!!step.onMarkerClick && !isCurrent}
              />
            </Flex>

            {!!step.onMarkerClick && !isCurrent ? (
              <Button variant='link' onClick={step.onMarkerClick} as={StyledStepText} tabIndex={-1}>
                {step.name}
              </Button>
            ) : (
              <Text variant={isCurrent ? 'h3' : undefined} as={StyledStepText}>
                {step.name}
              </Text>
            )}
          </Grid>
        );
      })}
    </StyledVerticalStepMarkers>
  );
};

export default VerticalFormProgress;
